define(
    'NMSDesignSystem.Cart.Shopping',
    [
        'Cart.Detailed.View',
        'Cart.Summary.View',
        'Cart.Lines.View',
        'Cart.Item.Summary.View',
        'Cart.Item.Actions.View',
        'QuickOrder.View',
        'QuickAdd.View',
        'nms_cart_detailed.tpl',
        'nms_cart_summary.tpl',
        'nms_cart_lines.tpl',
        'nms_cart_item_summary.tpl',
        'nms_cart_item_actions.tpl',
        'nms_quick_order.tpl',
        'nms_quick_add.tpl',
        'Utils',
        'underscore'
    ],
    function NMSDesignSystemCartShopping(
        CartDetailedView,
        CartSummaryView,
        CartLinesView,
        CartItemSummaryView,
        CartItemActionsView,
        QuickOrderView,
        QuickAddView,
        nmsCartDetailedTpl,
        nmsCartSummaryTpl,
        nmsCartLinesTpl,
        nmsCartItemSummaryTpl,
        nmsCartItemActionsTpl,
        nmsQuickOrderTpl,
        nmsQuickAddTpl,
        Utils,
        _
    ) {
        'use strict';

        var installed = false;
        var activeCartViews = 0;

        function formatProductName(value) {
            var acronyms = {
                CDC: true,
                CSOS: true,
                DEA: true,
                EMS: true,
                FDA: true,
                IM: true,
                IV: true,
                NDC: true,
                NMS: true,
                OTC: true,
                PPE: true,
                RX: true,
                SQ: true,
                USA: true,
                USP: true
            };
            var units = {
                BX: true,
                CC: true,
                EA: true,
                G: true,
                MCG: true,
                MG: true,
                ML: true,
                UM: true,
                VL: true
            };
            var text = String(value || '');
            var letters = text.replace(/[^A-Za-z]/g, '');

            if (!letters || letters !== letters.toUpperCase()) {
                return text;
            }

            return text.toLowerCase().replace(/[a-z0-9][a-z0-9'’/.#-]*/gi, function formatToken(token) {
                return token.split(/([/-])/).map(function formatPart(part) {
                    var upper = part.toUpperCase();
                    var unitMatch = upper.match(/^(\d+(?:\.\d+)?)([A-Z]+)$/);

                    if (part === '/' || part === '-') {
                        return part;
                    }
                    if (acronyms[upper] || units[upper]) {
                        return upper;
                    }
                    if (unitMatch && units[unitMatch[2]]) {
                        return unitMatch[1] + unitMatch[2];
                    }
                    if (/^\d+$/.test(part)) {
                        return part;
                    }

                    return part.charAt(0).toUpperCase() + part.slice(1);
                }).join('');
            });
        }

        function wrapContext(View, marker, decorate) {
            var prototype = View && View.prototype;
            var originalGetContext;

            if (!prototype || typeof prototype.getContext !== 'function' || prototype[marker]) {
                return;
            }

            originalGetContext = prototype.getContext;
            prototype.getContext = function getNMSCartContext() {
                return decorate.call(this, originalGetContext.apply(this, arguments));
            };
            prototype[marker] = true;
        }

        function restoreNativeCartRoot(view) {
            var element = view.el;
            var attributes;

            // A native Home-to-Cart transition may reuse Home's existing DOM
            // element. Backbone applies root attributes only to newly created
            // elements, leaving Home spacing rules active on the real Cart.
            if (!element || element.id !== 'home-page' || !element.classList) {
                return;
            }
            attributes = typeof view.attributes === 'function'
                ? view.attributes()
                : view.attributes;
            if (!attributes || typeof attributes.id !== 'string' ||
                !attributes.id || attributes.id === 'home-page') {
                return;
            }
            element.setAttribute('id', attributes.id);
            if (attributes['data-root-component-id']) {
                element.setAttribute('data-root-component-id', attributes['data-root-component-id']);
            }
            element.classList.remove('home-page');
        }

        function installCartPageLifecycle() {
            var prototype = CartDetailedView && CartDetailedView.prototype;
            var originalInitialize;
            var originalRender;
            var originalDestroy;

            if (!prototype || prototype.nmsNativeCartLifecycle) {
                return;
            }

            originalInitialize = prototype.initialize;
            originalRender = prototype.render;
            originalDestroy = prototype.destroy;

            prototype.initialize = function initializeNMSNativeCart() {
                originalInitialize.apply(this, arguments);
                if (!this.nmsNativeCartActive) {
                    this.nmsNativeCartActive = true;
                    activeCartViews += 1;
                }
                if (typeof document !== 'undefined' && document.body) {
                    document.body.classList.add('nms-cart-redesign-page');
                }
            };

            prototype.render = function renderNMSNativeCart() {
                restoreNativeCartRoot(this);
                var result = originalRender.apply(this, arguments);

                // A same-route cart navigation can initialize the incoming
                // view before destroying the outgoing one. Reassert the page
                // marker after each native render so the outgoing view cannot
                // leave the new cart on the legacy visual path.
                if (typeof document !== 'undefined' && document.body) {
                    document.body.classList.add('nms-cart-redesign-page');
                }

                return result;
            };

            prototype.destroy = function destroyNMSNativeCart() {
                var result = originalDestroy.apply(this, arguments);

                if (this.nmsNativeCartActive) {
                    this.nmsNativeCartActive = false;
                    activeCartViews = Math.max(0, activeCartViews - 1);
                }

                if (
                    typeof document !== 'undefined' &&
                    document.body &&
                    activeCartViews === 0
                ) {
                    document.body.classList.remove('nms-cart-redesign-page');
                }

                return result;
            };

            prototype.nmsNativeCartLifecycle = true;
        }

        function installQuantityNoopRecovery() {
            var prototype = CartDetailedView && CartDetailedView.prototype;
            var originalUpdate;

            if (!prototype || typeof prototype.updateItemQuantity !== 'function' ||
                prototype.nmsQuantityNoopRecovery) {
                return;
            }

            originalUpdate = prototype.updateItemQuantity;
            prototype.updateItemQuantity = function updateNMSCartItemQuantity(event) {
                var form = this.getItemForm(event);
                var input = form.find('[name="quantity"]');
                var row = form.closest('[data-type="order-item"]');
                var internalId = form.find('[name="internalid"]').val();
                var line = this.model.get('lines').get(internalId);
                var requested = parseInt(input.val(), 10);
                var isNoop = line && input[0] && !input.prop('disabled') &&
                    !this.isRemoving && this.validInputValue(input[0]) &&
                    requested === parseInt(line.get('quantity'), 10);
                var result = originalUpdate.apply(this, arguments);

                // Native quantity keypress/change handling disables line links
                // before its debounce. An unchanged quantity makes no request
                // or render, leaving those links disabled indefinitely. Restore
                // only that native marker after a valid no-op; pending updates,
                // removals, invalid inputs and other disabled links stay intact.
                if (isNoop && !this.isRemoving && !input.prop('disabled') &&
                    this.model.get('lines').get(internalId) === line &&
                    requested === parseInt(line.get('quantity'), 10) &&
                    row.attr('id') === internalId) {
                    row.find('a.cart-item-link-disabled')
                        .removeAttr('disabled')
                        .removeClass('cart-item-link-disabled');
                }

                return result;
            };
            prototype.nmsQuantityNoopRecovery = true;
        }

        function installQuickAddLanguage() {
            var prototype = QuickAddView && QuickAddView.prototype;
            var originalInitialize;

            if (!prototype || prototype.nmsNativeCartLanguage) {
                return;
            }

            originalInitialize = prototype.initialize;
            prototype.initialize = function initializeNMSQuickAdd() {
                originalInitialize.apply(this, arguments);
                if (this.itemsSearcherComponent && this.itemsSearcherComponent.options) {
                    this.itemsSearcherComponent.options.placeholderLabel = Utils.translate('Enter NDC/Item Number or product name');
                }
            };
            prototype.nmsNativeCartLanguage = true;
        }

        function installTemplates() {
            CartDetailedView.prototype.template = nmsCartDetailedTpl;
            CartSummaryView.prototype.template = nmsCartSummaryTpl;
            CartLinesView.prototype.template = nmsCartLinesTpl;
            CartItemSummaryView.prototype.template = nmsCartItemSummaryTpl;
            CartItemActionsView.prototype.template = nmsCartItemActionsTpl;
            QuickOrderView.prototype.template = nmsQuickOrderTpl;
            QuickAddView.prototype.template = nmsQuickAddTpl;
        }

        function installContexts() {
            wrapContext(CartDetailedView, 'nmsNativeCartDetailedContext', function decorateDetailed(context) {
                var itemCount = Number(context.itemCount || 0);

                return _.extend({}, context, {
                    pageHeader: 'Shopping Cart',
                    nmsItemCountLabel: itemCount === 1
                        ? Utils.translate('1 item')
                        : Utils.translate('$(0) items', itemCount)
                });
            });

            wrapContext(CartLinesView, 'nmsNativeCartLineContext', function decorateLine(context) {
                var item = this.model && this.model.get && this.model.get('item');
                var name = item && item.get && (item.get('storedisplayname2') || item.get('displayname') || item.get('_name'));

                return _.extend({}, context, {
                    nmsProductName: formatProductName(name)
                });
            });

            wrapContext(CartItemSummaryView, 'nmsNativeCartItemSummaryContext', function decorateItemSummary(context) {
                var line = context.line || {};
                var quantity = line.quantity;

                // The native summary view sometimes exposes the line model
                // without copying quantity into its template context. Keep the
                // template's standard line.quantity contract intact.
                if ((quantity === null || quantity === undefined) && this.model && this.model.get) {
                    quantity = this.model.get('quantity');
                }

                return _.extend({}, context, {
                    line: _.extend({}, line, {quantity: quantity})
                });
            });
        }

        return {
            mountToApp: function mountToApp() {
                if (installed) {
                    return;
                }

                installTemplates();
                installContexts();
                installCartPageLifecycle();
                installQuickAddLanguage();
                installQuantityNoopRecovery();
                installed = true;
            }
        };
    }
);
